import React, {useEffect, useState} from "react";
import s from "./AcceptMembersPage.module.css";
import { showError } from '../../../utils/errorHandler';

const AcceptMembersPage = () => {
  const [waitingMembers, setWaitingMembers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isOrgModalOpen, setIsOrgModalOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [revisionComment, setRevisionComment] = useState("");
  const [activeTab, setActiveTab] = useState('personal');

  useEffect(() => {
    fetchWaitingMembers();
    fetchOrganizations();
  }, []);

  const CompactField = ({ label, value }) => (
      <div className={s.compactField}>
        <label>{label}</label>
        <div>{value || 'Не указано'}</div>
      </div>
  );

  const fetchWaitingMembers = async () => {
    const response = await fetch("/api/users/waiting");
    const data = await response.json();
    setWaitingMembers(data);
  };

  const fetchOrganizations = async () => {
    const response = await fetch("/api/organizations");
    const data = await response.json();
    setOrganizations(data);
  };

  const handleOpenProfile = (member) => {
    setSelectedMember(member);
    setIsProfileModalOpen(true);
  };

  const handleReject = async () => {
    if (window.confirm("Вы уверены, что хотите отклонить заявку?")) {
      try {
        await fetch(`/api/users/${selectedMember.id}/reject`,
            {method: "DELETE"});
        fetchWaitingMembers();
        setIsProfileModalOpen(false);
      } catch (error) {
        console.error("Ошибка при отклонении:", error);
      }
    }
  };

  const handleRequestRevision = async () => {
    if (!revisionComment.trim()) {
      alert("Пожалуйста, введите комментарий");
      return;
    }

    try {
      const response = await fetch(
          `/api/users/${selectedMember.id}/request-revision`, {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({comment: revisionComment}),
          });

      if (response.ok) {
        fetchWaitingMembers();
        setIsProfileModalOpen(false);
        setRevisionComment("");
      }
    } catch (error) {
      console.error("Ошибка при отправке комментария:", error);
    }
  };

  const handleAccept = () => {
    // Только открываем модалку выбора организации
    setIsProfileModalOpen(false);
    setIsOrgModalOpen(true);
  };

  const handleAddToOrganization = async () => {
    if (!selectedOrganization) return;

    try {
      // 1. Принимаем пользователя
      const acceptResponse = await fetch(`/api/users/accept/${selectedMember.id}`, {
        method: "POST"
      });

      if (!acceptResponse.ok) {
        const errorText = await acceptResponse.text();
        showError(`Ошибка при принятии пользователя: ${errorText}`);
        return;
      }

      // 2. Добавляем в организацию
      const orgResponse = await fetch("/api/users-organizations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: selectedMember.id,
          organizationId: selectedOrganization.id,
        }),
      });

      if (!orgResponse.ok) {
        const errorText = await orgResponse.text();
        showError(`Ошибка при добавлении в организацию: ${errorText}`);
        return;
      }

      // Обновляем список и закрываем модалки
      fetchWaitingMembers();
      setIsOrgModalOpen(false);
    } catch (error) {
      console.error("Ошибка:", error);
      showError("Произошла ошибка при выполнении операции");
    }
  };

  return (
      <div className={s.acceptMembersPage}>
        <h1>Ожидающие вступления члены</h1>
        {waitingMembers.length > 0 ? (
          <ul className={s.memberList}>
            {waitingMembers.map((member) => (
                <li key={member.id} className={s.memberItem}>
                  {member.firstName} {member.lastName}
                  <button onClick={() => handleOpenProfile(member)}
                          className={s.profileBtn}>
                    Открыть профиль
                  </button>
                </li>
            ))}
          </ul>
        ) : (
          <div className={s.emptyState}>
            <p>Пока нет желающих вступить</p>
          </div>
        )}

        {isProfileModalOpen && (
            <div className={s.modal}>
              <div className={s.compactModalContent}>
                <div className={s.modalHeader}>
                  <h2>Профиль пользователя</h2>
                  <button className={s.closeButton} onClick={() => setIsProfileModalOpen(false)}>
                    &times;
                  </button>
                </div>

                <div className={s.scrollableTabs}>
                  <div className={s.tabs}>
                    {['Основное', 'Контакты', 'Документы', 'Родители', 'Дополнительно'].map((tab) => (
                        <button
                            key={tab}
                            className={`${s.tab} ${activeTab === tab ? s.activeTab : ''}`}
                            onClick={() => setActiveTab(tab)}
                        >
                          {tab}
                        </button>
                    ))}
                  </div>
                </div>

                <div className={s.modalBody}>
                  {activeTab === 'Основное' && (
                      <div className={s.twoColumnGrid}>
                        <CompactField label="Фамилия" value={selectedMember?.lastName} />
                        <CompactField label="Имя" value={selectedMember?.firstName} />
                        <CompactField label="Отчество" value={selectedMember?.patronymic} />
                        <CompactField label="Пол" value={selectedMember?.gender} />
                        <CompactField label="Дата рождения" value={selectedMember?.birthDate} />
                        <CompactField label="Место рождения" value={selectedMember?.birthPlace} />
                        <CompactField label="Национальность" value={selectedMember?.nationality} />
                      </div>
                  )}

                  {activeTab === 'Контакты' && (
                      <div className={s.twoColumnGrid}>
                        <CompactField label="Телефон" value={selectedMember?.phoneNumber} />
                        <CompactField label="Email" value={selectedMember?.email} />
                        <CompactField label="Адрес" value={selectedMember?.address} />
                        <CompactField label="Соцсети" value={selectedMember?.socialMedia} />
                        <CompactField label="Адрес регистрации" value={selectedMember?.registrationAddress} />
                      </div>
                  )}

                  {activeTab === 'Документы' && (
                      <div className={s.twoColumnGrid}>
                        <CompactField label="Серия паспорта" value={selectedMember?.passportSeries} />
                        <CompactField label="Номер паспорта" value={selectedMember?.passportNumber} />
                        <CompactField label="Кем выдан" value={selectedMember?.passportIssuedBy} />
                        <CompactField label="Дата выдачи" value={selectedMember?.passportIssueDate} />
                        <CompactField label="Образование" value={selectedMember?.education} />
                      </div>
                  )}

                  {activeTab === 'Родители' && (
                      <div className={s.twoColumnGrid}>
                        <CompactField label="ФИО родителя" value={selectedMember?.parentFullName} />
                        <CompactField label="Телефон родителя" value={selectedMember?.parentPhone} />
                        <CompactField label="Email родителя" value={selectedMember?.parentEmail} />
                      </div>
                  )}

                  {activeTab === 'Дополнительно' && (
                      <div className={s.twoColumnGrid}>
                        <CompactField label="Мотивация" value={selectedMember?.motivation} />
                        <CompactField label="Членство в организациях" value={selectedMember?.memberships} />
                        <CompactField label="Статус" value={selectedMember?.status} />
                        <CompactField label="Комментарий модератора" value={selectedMember?.revisionComment} />
                      </div>
                  )}
                </div>

                <div className={s.actionsSection}>
                    <textarea
                        className={s.commentInput}
                        placeholder="Введите комментарий для доработки..."
                        value={revisionComment}
                        onChange={(e) => setRevisionComment(e.target.value)}
                    />
                  <button
                      className={s.revisionButton}
                      onClick={handleRequestRevision}
                      disabled={!revisionComment.trim()}
                  >
                    Доработать
                  </button>
                  <div className={s.actionButtons}>
                    <button className={s.acceptButton} onClick={handleAccept}>
                      Принять
                    </button>
                    <button className={s.rejectButton} onClick={handleReject}>
                      Отклонить
                    </button>
                  </div>
                </div>
              </div>
            </div>
        )}

        {/* Модальное окно выбора организации */}
        {isOrgModalOpen && (
            <div className={s.modal}>
              <div className={s.orgModalContent}>
                <h2>Выберите организацию</h2>
                <div className={s.orgList}>
                  {organizations.map((org) => (
                      <div
                          key={org.id}
                          className={`${s.orgItem} ${
                              selectedOrganization?.id === org.id ? s.selected
                                  : ""
                          }`}
                          onClick={() => setSelectedOrganization(org)}
                      >
                        <span className={s.orgName}>{org.name}</span>
                        <span className={s.orgLocation}>{org.location}</span>
                      </div>
                  ))}
                </div>

                <div className={s.modalActions}>
                  <button
                      className={s.cancelBtn}
                      onClick={() => {
                        setIsOrgModalOpen(false);
                        setSelectedMember(null);
                      }}
                  >
                    Отмена
                  </button>
                  <button
                      className={s.confirmBtn}
                      onClick={handleAddToOrganization}
                      disabled={!selectedOrganization}
                  >
                    Подтвердить
                  </button>
                </div>
              </div>
            </div>
        )}
      </div>
  );
};

export default AcceptMembersPage;